// ============================================================
// DockerSecurity.jsx
// ============================================================
// Junior-dev Notes:
// - Concept: Containers share the Host kernel. Isolation is key.
// - Key Term: "Non-Root User" (Don't run apps as root!).
// - Strategy: Visualize the defensive layers of a container.
// - Branding: prodevconsult.com
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import ShieldIcon from "@mui/icons-material/Shield";
import LockIcon from "@mui/icons-material/Lock";
import BugReportIcon from "@mui/icons-material/BugReport";
import GppGoodIcon from "@mui/icons-material/GppGood";
import NoEncryptionGmailerrorredIcon from "@mui/icons-material/NoEncryptionGmailerrorred";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#0DB7ED";
const NAVY_BG = "#001E2B";
const TERMINAL_BG = "#011627";
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.6)",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(13, 183, 237, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const SecurityCard = styled(Paper)(({ theme, severity }) => ({
    padding: theme.spacing(3),
    backgroundColor: "rgba(255,255,255,0.02)",
    borderTop: `4px solid ${severity === 'high' ? '#FF5252' : DOCKER_BLUE}`,
    borderRadius: "8px",
    height: "100%",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(255,255,255,0.05)",
        transform: "translateY(-5px)"
    }
}));

const DockerfileSnippet = styled(Paper)(({ theme }) => ({
    backgroundColor: TERMINAL_BG,
    padding: theme.spacing(2),
    borderRadius: "8px",
    border: `1px solid ${BRAND.border}`,
    fontFamily: "'Fira Code', monospace",
    fontSize: '0.85rem',
    marginTop: theme.spacing(2)
}));

export default function DockerSecurity() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="Production · Module 12"
                            sx={{ bgcolor: "rgba(13, 183, 237, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Security <span style={{ color: DOCKER_BLUE }}>Best Practices</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        A container is only as secure as its configuration. Protect your
                        infrastructure by following industry-standard hardening techniques.
                    </Typography>
                </Box>

                {/* 2. THE SECURITY STACK VISUAL */}


                <Grid container spacing={4} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h5" fontWeight={800} sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <ShieldIcon sx={{ color: DOCKER_BLUE }} /> Hardening the Dockerfile
                        </Typography>
                        <Typography variant="body2" sx={{ color: BRAND.muted, mb: 2 }}>
                            By default, Docker containers run as <strong>root</strong>. If an attacker escapes the container, they have full control of your server. Always switch to a non-privileged user.
                        </Typography>

                        <DockerfileSnippet>
                            <Typography sx={{ color: "rgba(255,255,255,0.3)", mb: 1 }}># Create a system user</Typography>
                            <Typography sx={{ color: "#82AAFF", mb: 0.5 }}>RUN adduser -D appuser</Typography>
                            <Typography sx={{ color: "#82AAFF", mb: 1 }}>WORKDIR /app</Typography>
                            <Typography sx={{ color: "rgba(255,255,255,0.3)", mb: 1 }}># Switch to the new user</Typography>
                            <Typography sx={{ color: "#C792EA", fontWeight: 'bold' }}>USER appuser</Typography>
                            <Typography sx={{ color: "#82AAFF", mt: 1 }}>CMD ["./myapp"]</Typography>
                        </DockerfileSnippet>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Stack spacing={3} justifyContent="center" sx={{ height: '100%' }}>
                            <Paper sx={{ p: 3, bgcolor: "rgba(13, 183, 237, 0.05)", borderRadius: 2, border: `1px solid ${BRAND.border}` }}>
                                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LockIcon fontSize="small" /> Read-Only Filesystem
                                </Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    Prevent attackers from downloading scripts or modifying your code by running with <code>--read-only</code>.
                                </Typography>
                            </Paper>
                            <Paper sx={{ p: 3, bgcolor: "rgba(0, 237, 100, 0.05)", borderRadius: 2, border: "1px solid rgba(0, 237, 100, 0.2)" }}>
                                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <GppGoodIcon fontSize="small" /> Official Base Images
                                </Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>
                                    Only pull images from <strong>Verified Publishers</strong> to avoid pre-installed malware or backdoors.
                                </Typography>
                            </Paper>
                        </Stack>
                    </Grid>
                </Grid>

                {/* 3. DEFENSE IN DEPTH GRID */}
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 6, textAlign: "center" }}>
                    The <span style={{ color: DOCKER_BLUE }}>Defense</span> Checklist
                </Typography>

                <Grid container spacing={3} sx={{ mb: 10 }}>
                    {[
                        {
                            title: "Vulnerability Scanning",
                            icon: <BugReportIcon />,
                            desc: "Use 'docker scan' or tools like Snyk to find known security holes in your base images and libraries."
                        },
                        {
                            title: "Resource Limits",
                            icon: <FitnessCenterIcon />,
                            desc: "Set CPU and RAM limits to prevent 'Denial of Service' (DoS) attacks from crashing your host machine."
                        },
                        {
                            title: "No Secrets in Images",
                            icon: <NoEncryptionGmailerrorredIcon />,
                            desc: "Never hardcode passwords or API keys. Use Docker Secrets or Environment Variables instead."
                        }
                    ].map((item, i) => (
                        <Grid item xs={12} md={4} key={i}>
                            <SecurityCard>
                                <Box sx={{ color: DOCKER_BLUE, mb: 2 }}>{item.icon}</Box>
                                <Typography variant="h6" fontWeight={800} gutterBottom>{item.title}</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.6 }}>
                                    {item.desc}
                                </Typography>
                            </SecurityCard>
                        </Grid>
                    ))}
                </Grid>

                {/* 4. THE GOLDEN RULE */}
                <Paper sx={{
                    p: 4,
                    bgcolor: "rgba(255, 82, 82, 0.05)",
                    border: "1px dashed #FF5252",
                    borderRadius: 4,
                    textAlign: 'center'
                }}>
                    <Typography variant="h6" fontWeight={900} sx={{ color: "#FF5252", mb: 2 }}>
                        ⚠️ THE GOLDEN RULE OF DOCKER SECURITY
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 400, fontStyle: 'italic', color: TEXT_WHITE }}>
                        "If you don't need it to run the app, don't include it in the image."
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2, color: BRAND.muted }}>
                        This is why <strong>Multi-Stage builds</strong> and <strong>Alpine Linux</strong> are so important.
                        Less code = Fewer bugs = Better security.
                    </Typography>
                </Paper>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 5. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>Infrastructure Hardened. Let's wrap up.</Typography>
                    <Link to="/docker-conclusion" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 8,
                                py: 2,
                                borderRadius: "4px",
                                boxShadow: `0 4px 14px rgba(13, 183, 237, 0.3)`,
                                "&:hover": { bgcolor: "#0091ca" }
                            }}
                        >
                            Final Chapter: Summary
                        </Button>
                    </Link>
                    <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.3 }}>
                        Authorized Content by {BRAND.domain}
                    </Typography>
                </Box>

            </Container>
        </PageWrapper>
    );
}
